import { useCallback, useEffect, useMemo, useState } from "react";
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Fade from '@mui/material/Fade'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Tooltip from '@mui/material/Tooltip'
import { useNavigate } from "react-router";
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { alpha, useTheme } from '@mui/material/styles';
import menuItems from './menuItems'
import { usePermissions } from "@/hooks/usePermissions";
import { useGetDetailsQuery } from "@/api";
import { useTranslation } from "react-i18next";
import { setIsSidebarOpen } from "@/features/layout/layoutSlice";
import { useDispatch } from "react-redux";
import { Link as RouterLink, useLocation } from 'react-router'
import useLayout from "@/features/layout/useLayout";

const VerticalMenuNavigation = ({ searchQuery }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { isMobile, isSidebarCollapsed } = useLayout()
  const navigate = useNavigate();
  const { hasPermission } = usePermissions()
  const { data: user, isSuccess } = useGetDetailsQuery()
  const [openGroups, setOpenGroups] = useState({});
  const { pathname } = useLocation()
    
  const toggleGroup = (path) => {
    setOpenGroups((prev) => ({
      ...prev,
      [path]: !prev[path]
    }))
  }
  const canAccessItem = useCallback(
    (item) => {
      const byAdminTitle = item.adminByTitle
        ? isSuccess &&
          user?.roles?.some(
            (r) =>
              r?.title?.toLowerCase() === item.adminByTitle.toLowerCase()
          )
        : null

      const permissions = Array.isArray(item.permission)
        ? item.permission
        : item.permission
          ? [item.permission]
          : []
      const byPermission = permissions.some((permission) =>
        hasPermission(permission)
      )

      if (item.adminByTitle && permissions.length) {
        return byAdminTitle || byPermission
      }
      if (item.adminByTitle) return byAdminTitle
      if (permissions.length) return byPermission
      return true
    },
    [hasPermission, isSuccess, user]
  );
  
  const filtered = useMemo(() => {
    const queryValue = searchQuery.toLowerCase().trim()

    const filterItems = (items) =>
      items.reduce((acc, item) => {
        const allowed = canAccessItem(item)
        if (!allowed) return acc

        const childItems = Array.isArray(item.children)
          ? filterItems(item.children)
          : []

        const label = (t(item.labelKey) || item.labelKey || item.path || '')
          .toLowerCase()
        const matches = !queryValue || label.includes(queryValue)

        if (matches || childItems.length > 0) {
          acc.push({
            ...item,
            children: childItems
          })
        }
        return acc
      }, [])

    return filterItems(menuItems)
  }, [canAccessItem, searchQuery, t])

  const closeOnMobile = () => {
    if (isMobile) dispatch(setIsSidebarOpen(false))
  }

  const isItemSelected = (path) => (pathname === path) || pathname.startsWith(`${path}/`);

  const selectedItemStyles = {
    bgcolor: theme.palette.sidebar.selectedBg,
    color: theme.palette.sidebar.selectedColor,
    '&:hover': {
      bgcolor: theme.palette.sidebar.selectedHover
    }
  }

  useEffect(() => {
    setOpenGroups((prev) => {
      let changed = false
      const next = { ...prev }

      filtered.forEach((item) => {
        if (!item.children?.length) return

        const isActiveGroup = pathname.startsWith(`${item.path}/`)
        if (isActiveGroup && typeof next[item.path] === 'undefined') {
          next[item.path] = true
          changed = true
        }
      })

      return changed ? next : prev
    })
  }, [filtered, pathname])

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pb: 2 }}>
      <List sx={{ px: 1 }}>
        {
          filtered.map((item) => {
            const selected = isItemSelected(item.path)
            const hasChildren = item.children?.length > 0
            const groupOpen = openGroups[item.path] ?? selected
            if (!hasChildren) {
              return (
                <Tooltip
                  key={item.path}
                  title={!isSidebarCollapsed ? t(item.labelKey) : ''}
                  placement="right"
                >
                  <ListItemButton
                    disableGutters
                    component={RouterLink}
                    nativeButton={false}
                    to={item.path}
                    onClick={closeOnMobile}
                    selected={selected}
                    sx={(theme) => ({
                      borderRadius: 2,
                      px: 1.25,
                      py: isSidebarCollapsed ? 0.5 : 1.25,
                      m: 0.5,
                      // marginRight: 'auto',
                      // mx: isSidebarCollapsed ? undefined : 0.5,
                      // width: isSidebarCollapsed ? '100%' : 'auto',
                      transition: 'all 0.25s ease',
                      '&.Mui-selected': selectedItemStyles,
                      '&:hover': { bgcolor: theme.palette.sidebar.itemHover }
                    })}
                  >
                    <ListItemIcon
                      sx={(theme) => ({
                        minWidth: isSidebarCollapsed ? 38 : 4,
                        color: selected ? theme.palette.sidebar.selectedColor : 'inherit'
                      })}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {
                      isSidebarCollapsed ? (
                        <Fade in timeout={150}>
                          <ListItemText
                            primary={t(item.labelKey)}
                            slotProps={{ primary: {
                              noWrap: true,
                              fontSize: '0.92rem',
                              fontWeight: selected ? 600 : 500
                            }}}
                          />
                        </Fade>
                      ) : null
                    }
                  </ListItemButton>
                </Tooltip>
              )
            }

            return (
              <Box key={item.path}>
                <Tooltip title={!isSidebarCollapsed ? t(item.labelKey) : ''} placement="right">
                  <ListItemButton
                    onClick={() => {
                      if (!isSidebarCollapsed) {
                        navigate(item.children[0]?.path || item.path)
                        closeOnMobile()
                        return
                      }
                      toggleGroup(item.path)
                      if (!selected && item.path) {
                        navigate(item.path)
                      }
                    }}
                    selected={selected}
                    sx={(theme) => ({
                      borderRadius: 2,
                      px: 1.25,
                      py: isSidebarCollapsed ? 0.5 : 1.25,
                      m: 0.5,
                      // marginRight: 'auto',
                      // mx: isSidebarCollapsed ? undefined : 0.5,
                      // width: isSidebarCollapsed ? '100%' : 'auto',
                      transition: 'all 0.25s ease',
                      '&.Mui-selected': selectedItemStyles,
                      '&:hover': { bgcolor: theme.palette.sidebar.itemHover }
                    })}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: isSidebarCollapsed ? 38 : 4,
                        color: selected ? theme.palette.sidebar.selectedColor : 'inherit'
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {isSidebarCollapsed ? (
                      <>
                        <Fade in timeout={150}>
                          <ListItemText
                            primary={t(item.labelKey)}
                            slotProps={{
                              primary: {
                                noWrap: true,
                                fontSize: '0.92rem',
                                fontWeight: selected ? 600 : 500
                              }
                            }}
                          />
                        </Fade>
                        {groupOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </>
                    ) : null}
                  </ListItemButton>
                </Tooltip>
                <Collapse in={isSidebarCollapsed && groupOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => {
                      const childSelected = isItemSelected(child.path)
                      return (
                        <ListItemButton
                          key={child.path}
                          component={RouterLink}
                          nativeButton={false}
                          to={child.path}
                          onClick={closeOnMobile}
                          selected={childSelected}
                          sx={(theme) => ({
                            mb: 0.5,
                            borderRadius: 2,
                            pl: 5.5,
                            pr: 1.25,
                            minHeight: 38,
                            '&.Mui-selected': {
                              bgcolor: alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === 'dark' ? 0.18 : 0.12
                              ),
                              color: theme.palette.sidebar.selectedColor,
                              '&:hover': {
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  theme.palette.mode === 'dark' ? 0.24 : 0.18
                                )
                              }
                            },
                            '&:hover': { bgcolor: theme.palette.sidebar.itemHover }
                          })}
                        >
                          <ListItemText
                            primary={t(child.labelKey)}
                            slotProps={{
                              primary: {
                                noWrap: true,
                                fontSize: '0.88rem',
                                fontWeight: childSelected ? 600 : 500
                              }
                            }}
                          />
                        </ListItemButton>
                      )
                    })}
                  </List>
                </Collapse>
              </Box>
            )
          })
        }
      </List>
    </Box>
  )
}

export default VerticalMenuNavigation;
